import { Box, useMediaQuery } from '@mui/material'
import { useContext, useEffect, useState } from 'react';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import gaiaTheme from '../../../root-components/theme';
import { IHeadCell } from '../../../shared/interfaces/table-display.model';
import { SettingsMobileNavbar } from '../../../root-components/navigation/settings-mobile-navbar';
import { TableDisplay } from '../../presentation-components/miscellaneous-components/table-display';
import { TableActionButtons } from '../../presentation-components/miscellaneous-components/table-action-buttons';
import { LoadingPage } from '../loading-page';
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container';
import { IconName } from '../../../shared/interfaces/icon-picker.model';
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading';
import CustomFieldService from '../../../shared/services/custom-field/custom-field.service';
import { ICustomField, IPostCustomField } from '../../../shared/services/custom-field/custom-field.model';
import { CustomFieldDialog } from '../../dialogs/settings/custom-field-dialog';
import { ScreenLabelsContext } from '../../../context/screen-labels/screen-labels';

export const SettingsCustomFieldsPage = () => {
  //Services
  const customFieldService = new CustomFieldService()
  //Context
  const userNotificationsContext = useContext(UserNotificationsContext)
  const screenLabelsContext = useContext(ScreenLabelsContext)
  //State
  const [customFields, setCustomFields] = useState<ICustomField[]>()
  const [customFieldId, setCustomFieldId] = useState<string>()
  const [openDialog, setOpenDialog] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>()
  const [errorCode, setErrorCode] = useState<number>()
  //Table State
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [selected, setSelected] = useState<string[]>([]);
  const [change, setChange] = useState<number>(0)
  const [orderBy, setOrderBy] = useState<string>()
  const [order, setOrder] = useState<'asc' | 'desc'>()
  //Loading State
  const [hasPageMounted, setHasPageMounted] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isDialogSubmitting, setIsDialogSubmitting] = useState<boolean>(false)
  //Layout
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));
  //Use Effects
  useEffect(() => {
    getAllCustomFields()
  }, [inputPage, perPage, change])

  useEffect(() => {
    if (hasPageMounted) {
      if (inputPage === 0) {
        setChange(change + 1)
      } else {
        setInputPage(0)
      }
    }
    setHasPageMounted(true)
  }, [order, orderBy])

  const getAllCustomFields = () => {
    customFieldService.getAllWithPagination(inputPage, perPage, orderBy, order)
      .then(response => {
        setIsLoading(false)
        setTotalRecords(response.count);
        setCustomFields(response.rows)
      })
      .catch(err => {
        setIsLoading(false)
        console.log(err)
      })
  }
  //Table Contents
  const headCells: IHeadCell[] = [
    { id: 'action', numeric: false, label: 'Action', sortable: false, includeInMobile: false },
    { id: 'fieldName', numeric: false, label: 'Field Name', sortable: true, isHeading: true },
    { id: 'fieldType', numeric: false, label: 'Field Type', sortable: true },
    { id: 'entryType', numeric: false, label: 'Entry Type', sortable: true },
    { id: 'active', numeric: false, label: 'Active', sortable: false },
  ];

  const rows = !customFields ? [] : customFields.map((customField) => {
    return {
      id: customField.id,
      fieldName: customField.fieldName,
      fieldType: customField.fieldType.charAt(0).toUpperCase() + customField.fieldType.slice(1).toLowerCase(),
      entryType: customField.entryType === 'transaction' ? 'Emissions' :
        customField.entryType === 'project' ? screenLabelsContext.getLabel('project', { plural: true, capitalize: 'first' }) :
          customField.entryType === 'contactSupplier' ? screenLabelsContext.getLabel('supplier', { plural: true, capitalize: 'first' }) :
            customField.entryType === 'capitalGood' ? screenLabelsContext.getLabel('product', { plural: true, capitalize: 'first' }) :
              customField.entryType === 'vehicle' ? screenLabelsContext.getLabel('vehicle', { plural: true, capitalize: 'first' }) :
                customField.entryType === 'location' ? screenLabelsContext.getLabel('location', { plural: true, capitalize: 'first' }) :
                  customField.entryType === 'subsidiary' ? screenLabelsContext.getLabel('subsidiary', { plural: true, capitalize: 'first' }) :
                    null,
      active: customField.active ? 'Active' : 'Inactive',
      action: (
        customField.active ? (
          <TableActionButtons
            id={customField.id}
            includeEdit={true}
            includeDeactivate={true}
            onEdit={() => editClickHandler(customField.id)}
            onDeactivate={() => changeActiveStatus(customField.id, false)}
          />
        ) : (
          <TableActionButtons
            id={customField.id}
            includeEdit={true}
            onEdit={() => editClickHandler(customField.id)}
            includeOther={true}
            otherButtonIcon={IconName.Autorenew}
            otherButtonText={'Activate'}
            onOther={() => changeActiveStatus(customField.id, true)}
          />
        )
      )
    }
  })

  //Table Event Handlers
  const handleOrderChange = (orderBy: string, order: 'asc' | 'desc',) => {
    setOrderBy(orderBy)
    setOrder(order)
  };

  const editClickHandler = (customFieldId: string) => {
    setCustomFieldId(customFieldId)
    setOpenDialog(true)
  }

  const changeActiveStatus = (customFieldId: string, active: boolean) => {
    customFieldService.changeActiveStatus(customFieldId, active)
      .then(response => {
        getAllCustomFields()
        userNotificationsContext.openSnackbar({ open: true, message: `Updated successfully` });
      })
      .catch(err => {
        console.log(err)
        userNotificationsContext.openSnackbar({ open: true, message: `There was an error, please try again`, backgroundColor: 'text.primary' })
      })
  }

  // Dialog Event Handlers
  const submitDialogHandler = (values: IPostCustomField, resetForm: () => void, isEdit: boolean,) => {
    if (!isEdit) {
      if (!isDialogSubmitting) {
        setIsDialogSubmitting(true)
        customFieldService.create(values)
          .then(response => {
            getAllCustomFields()
            resetForm();
            setOpenDialog(false)
            userNotificationsContext.openSnackbar({ open: true, message: `Saved successfully` });
            setIsDialogSubmitting(false)
          })
          .catch(err => {
            if (err.response.data.message) {
              setErrorMessage(err.response.data.message)
              setErrorCode(err.response.data.code)
            } else {
              userNotificationsContext.openSnackbar({ open: true, message: `There was an error, please try again`, backgroundColor: 'text.primary' })
            }
            setIsDialogSubmitting(false)
          })
      }
    }
    if (isEdit && customFieldId) {
      if (!isDialogSubmitting) {
        setIsDialogSubmitting(true)
        customFieldService.update(customFieldId, values)
          .then(response => {
            getAllCustomFields()
            resetForm();
            setOpenDialog(false)
            setCustomFieldId('')
            userNotificationsContext.openSnackbar({ open: true, message: `Updated successfully` });
            setIsDialogSubmitting(false)
          })
          .catch(err => {
            if (err.response.data.message) {
              setErrorMessage(err.response.data.message)
              setErrorCode(err.response.data.code)
            } else {
              userNotificationsContext.openSnackbar({ open: true, message: `There was an error, please try again`, backgroundColor: 'text.primary' })
            }
            setIsDialogSubmitting(false)
          })
      }
    }
  }

  const closeDialogHandler = () => {
    setOpenDialog(false)
    setCustomFieldId('')
  }

  // Curated product passport attribute set (material composition, recycled content, recyclability,
  // weight, country of manufacture). Idempotent on the backend: existing names are left alone.
  const addPassportFieldsHandler = () => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Add product passport fields',
      message: `This adds a curated set of ${screenLabelsContext.getLabel('product')} fields for passport-relevant data: material composition, recycled content, recyclability, weight and country of manufacture. Fields you already have are left unchanged.`,
      buttonCopy: 'Add the fields',
      cancelButtonCopy: 'Cancel',
    }).then((confirmed) => {
      if (!confirmed) return
      customFieldService.seedPassportTemplate()
        .then((result) => {
          getAllCustomFields()
          const message = result.created.length > 0
            ? `Added ${result.created.length} passport field${result.created.length === 1 ? '' : 's'}${result.existing.length > 0 ? ` (${result.existing.length} already existed)` : ''}`
            : 'All passport fields already exist'
          userNotificationsContext.openSnackbar({ open: true, message })
        })
        .catch(err => {
          console.log(err)
          userNotificationsContext.openSnackbar({ open: true, message: `There was an error, please try again`, backgroundColor: 'text.primary' })
        })
    })
  }

  return (
    <PageOuterContainer>
      {isMobileMode && <Box sx={{ pb: 4 }}>
        <SettingsMobileNavbar />
      </Box>}
      <PageHeading
        title='Custom Fields'
        includeActionButton={true}
        actionButtonIcon={IconName.Add}
        actionButtonHandler={() => { setOpenDialog(true) }}
        actionButtonText='Add Custom Field'
        includeSecondaryActionButton={true}
        secondaryActionButtonIcon={IconName.Inventory2Outlined}
        secondaryActionButtonHandler={addPassportFieldsHandler}
        secondaryActionButtonText='Add Product Passport Fields'
        hasTabs={false}
      />
      <LoadingPage pageReady={!isLoading}>
        {customFields &&
          <TableDisplay
            onOrderChange={handleOrderChange}
            isMobileMode={isMobileMode}
            headCells={headCells}
            rows={rows}
            selected={selected}
            setSelected={setSelected}
            selectByIcon={true}
            inputPage={inputPage}
            setInputPage={setInputPage}
            totalRecords={totalRecords}
            perPage={perPage}
            setPerPage={setPerPage} />}
      </LoadingPage>
      {!customFieldId && openDialog &&
        <CustomFieldDialog
          onSubmit={submitDialogHandler}
          open={openDialog}
          errorMessage={errorMessage}
          errorCode={errorCode}
          isSubmitting={isDialogSubmitting}
          closeHandler={closeDialogHandler}
          isEdit={false} />}
      {customFieldId && openDialog &&
        <CustomFieldDialog
          isSubmitting={isDialogSubmitting}
          onSubmit={submitDialogHandler}
          open={openDialog}
          errorMessage={errorMessage}
          errorCode={errorCode}
          closeHandler={closeDialogHandler}
          isEdit={true}
          customFieldId={customFieldId} />}
    </PageOuterContainer>
  )
}